import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import WeightChart from '../../components/WeightChart';
import FileUpload from '../../components/FileUpload';
import * as progressApi from '../../api/progress.api';
import { errorMessage } from '../../api/client';

const KINDS = ['BEFORE', 'WEEKLY', 'AFTER', 'LAB_RESULT'];

// Clinic / staff page to upload progress entries against a booking.
// Mounted at /progress/orders/:orderId.

function PatientProgress() {
  const { orderId } = useParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    kind: 'WEEKLY', weekIndex: '', weightKg: '', bmi: '',
    labTestName: '', labValue: '', notes: '',
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  function set(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  async function load() {
    setLoading(true);
    try {
      const list = await progressApi.listProgressForOrder(orderId);
      setEntries(list || []);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  async function submit(e) {
    e.preventDefault();
    if (!form.weightKg && form.kind !== 'LAB_RESULT') {
      toast.error('Weight is required for non-lab entries.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      if (file) fd.append('file', file);
      await progressApi.createProgress(orderId, fd);
      toast.success('Progress recorded');
      setForm({ kind: 'WEEKLY', weekIndex: '', weightKg: '', bmi: '', labTestName: '', labValue: '', notes: '' });
      setFile(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="User progress" subtitle={`Booking ${orderId}`} />

      <div className="card">
        <h3>Add entry</h3>
        <form onSubmit={submit}>
          <div className="grid grid-3" style={{ gap: 'var(--s-3)' }}>
            <div className="field">
              <label>Type</label>
              <select className="input" value={form.kind} onChange={(e) => set('kind', e.target.value)}>
                {KINDS.map((k) => <option key={k}>{k}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Week index</label>
              <input className="input" type="number" min="0" value={form.weekIndex} onChange={(e) => set('weekIndex', e.target.value)} />
            </div>
            <div className="field">
              <label>Weight (kg)</label>
              <input className="input" type="number" step="0.1" value={form.weightKg} onChange={(e) => set('weightKg', e.target.value)} />
            </div>
            <div className="field">
              <label>BMI</label>
              <input className="input" type="number" step="0.1" value={form.bmi} onChange={(e) => set('bmi', e.target.value)} />
            </div>
            {form.kind === 'LAB_RESULT' && (
              <>
                <div className="field">
                  <label>Lab test</label>
                  <input className="input" value={form.labTestName} onChange={(e) => set('labTestName', e.target.value)} placeholder="HbA1c" />
                </div>
                <div className="field">
                  <label>Value</label>
                  <input className="input" value={form.labValue} onChange={(e) => set('labValue', e.target.value)} placeholder="5.6%" />
                </div>
              </>
            )}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea className="input" rows="2" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Photo / lab attachment (optional)</label>
              <FileUpload
                value={file}
                onChange={setFile}
                accept=".pdf,.jpg,.jpeg,.png"
                maxBytes={10 * 1024 * 1024}
              />
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy}>Save entry</button>
        </form>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {entries.some((e) => typeof e.weightKg === 'number') && (
            <div className="card" style={{ marginTop: 'var(--s-4)' }}>
              <WeightChart entries={entries} />
            </div>
          )}

          <div className="card" style={{ marginTop: 'var(--s-4)' }}>
            <h3>All entries</h3>
            {entries.length === 0 ? (
              <p className="muted">No entries yet.</p>
            ) : (
              <table className="table">
                <thead><tr><th>When</th><th>Type</th><th>Week</th><th>Weight</th><th>BMI</th><th>Notes</th></tr></thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e._id}>
                      <td>{new Date(e.recordedAt || e.createdAt).toLocaleString()}</td>
                      <td><span className="badge">{e.kind}</span></td>
                      <td>{e.weekIndex || 0}</td>
                      <td>{e.weightKg ? `${e.weightKg} kg` : '—'}</td>
                      <td>{e.bmi || '—'}</td>
                      <td className="muted text-sm">{e.labTestName ? `${e.labTestName}: ${e.labValue}` : e.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default PatientProgress;
